import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { MagnifyingGlass, Play, ArrowRight, FlowArrow, Trash, Plus } from '@phosphor-icons/react'
import { motion } from 'framer-motion'
import { Card, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { cn } from '@/lib/utils'
import { getErrorMessage, workflowTemplatesApi } from '@/lib/api'
import { usePermission } from '@/hooks/usePermission'
import { useAuthStore } from '@/stores/authStore'
import { mergeWorkflowTemplates, type GalleryTemplate } from './mergeWorkflowTemplates'
import {
  WORKFLOW_TEMPLATES,
  WORKFLOW_CATEGORY_LABELS,
  WORKFLOW_CATEGORY_COLORS,
  type WorkflowTemplateCategory,
} from './workflowTemplates'

const WorkflowTemplateGallery: React.FC = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  // Whoever can create a workflow can create a template -- this deliberately
  // reuses workflows.manage rather than a separate cell. "draft" (Rep) is
  // write-capable but only over templates they created themselves; "all"
  // (Owner/Admin/Manager) can manage every template in the account; "view"
  // (Analyst) can only list/use.
  const { scope: templateScope, canWrite: canCreateTemplates } = usePermission('workflows', 'manage')
  const currentUserId = useAuthStore((s) => s.user?.id)
  const canDeleteTemplate = (template: GalleryTemplate) => {
    if (templateScope === 'all') return true
    if (templateScope === 'draft') return template.createdByUserId === currentUserId
    return false
  }
  const [searchQuery, setSearchQuery] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<WorkflowTemplateCategory | ''>('')
  const [deleteTarget, setDeleteTarget] = useState<GalleryTemplate | null>(null)

  const { data: customTemplates = [] } = useQuery({
    queryKey: ['workflow-templates', 'custom'],
    queryFn: () => workflowTemplatesApi.list().then((res) => res.data),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => workflowTemplatesApi.delete(id),
    onSuccess: () => {
      toast.success('Template deleted')
      queryClient.invalidateQueries({ queryKey: ['workflow-templates', 'custom'] })
      setDeleteTarget(null)
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Failed to delete template'))
    },
  })

  const allTemplates = mergeWorkflowTemplates(customTemplates, WORKFLOW_TEMPLATES)

  const filtered = allTemplates.filter((t) => {
    if (categoryFilter && t.category !== categoryFilter) return false
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      return (
        t.name.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        t.tags.some((tag) => tag.toLowerCase().includes(q))
      )
    }
    return true
  })

  // Open the builder pre-loaded with the template as an UNSAVED draft. We
  // deliberately do not persist here — creating from a template is allowed
  // even if a workflow with the same name already exists. The duplicate-name
  // check applies at save time (backend 409), so the user must rename before
  // the workflow can be saved.
  const handleUseTemplate = (template: GalleryTemplate) => {
    navigate('/workflows/builder', {
      state: {
        from: 'templates',
        template: {
          name: template.name,
          description: template.description,
          nodes: template.nodes,
          edges: template.edges,
        },
      },
    })
  }

  return (
    <div className="space-y-5">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="flex-1 max-w-xl">
          <Input
            placeholder="Search workflow templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            icon={<MagnifyingGlass className="h-4 w-4" weight="regular" />}
          />
        </div>
        <Select
          value={categoryFilter}
          onChange={(v) => setCategoryFilter(v as WorkflowTemplateCategory | '')}
          ariaLabel="Filter by category"
          minWidth={180}
          options={[
            { value: '', label: 'All categories' },
            ...Object.entries(WORKFLOW_CATEGORY_LABELS).map(([value, label]) => ({ value, label })),
          ]}
        />
        {canCreateTemplates && (
          <Button
            variant="default"
            size="sm"
            icon={<Plus className="h-4 w-4" weight="regular" />}
            onClick={() => navigate('/workflows/builder', { state: { from: 'templates', templateAuthoring: true } })}
            className="lg:ml-auto"
          >
            Create template
          </Button>
        )}
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <Card variant="default" className="rounded-lg border-border/50 dark:bg-white/[0.02] dark:border-white/[0.08]">
          <CardContent className="p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mx-auto mb-4">
                <FlowArrow className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" weight="regular" />
              </div>
              <h3
                className="text-foreground dark:text-[#f7f8f8] mb-2"
                style={{
                  fontSize: '20px',
                  fontWeight: 590,
                  lineHeight: 1.33,
                  letterSpacing: '-0.24px',
                }}
              >
                No templates found
              </h3>
              <p
                className="text-muted-foreground dark:text-[#8a8f98]"
                style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}
              >
                Try adjusting your search or category filter.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((template) => {
            const Icon = template.icon
            return (
              <motion.div
                key={template.id}
                whileHover={{ y: -2 }}
                transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                className="h-full"
              >
                <Card
                  variant="default"
                  className="relative h-full rounded-lg border-border/50 transition-colors duration-200 hover:border-border/80 dark:bg-white/[0.02] dark:border-white/[0.08] dark:hover:bg-white/[0.04] dark:hover:border-[#7170ff]/25 flex flex-col"
                >
                  <CardContent className="p-5 flex flex-col flex-1">
                    {/* Header */}
                    <div className="flex items-start gap-3 mb-3">
                      <div className="h-10 w-10 rounded-md bg-[#5e6ad2]/10 border border-[#5e6ad2]/15 dark:bg-[#5e6ad2]/[0.12] dark:border-[#5e6ad2]/20 flex items-center justify-center flex-shrink-0">
                        <Icon className="h-5 w-5 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="regular" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3
                          className="text-foreground dark:text-[#f7f8f8] leading-snug"
                          style={{ fontSize: '15px', fontWeight: 510, letterSpacing: '-0.182px' }}
                        >
                          {template.name}
                        </h3>
                        <span className="flex flex-wrap items-center gap-1.5 mt-1">
                          <span
                            className={cn(
                              'inline-block px-2 py-0.5 rounded-full',
                              WORKFLOW_CATEGORY_COLORS[template.category],
                            )}
                            style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
                          >
                            {WORKFLOW_CATEGORY_LABELS[template.category]}
                          </span>
                          {template.source === 'custom' && (
                            <span
                              className="inline-block px-2 py-0.5 rounded-full bg-[#5e6ad2]/10 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.15] dark:text-[#a5b4fc]"
                              style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
                            >
                              Custom
                            </span>
                          )}
                        </span>
                      </div>
                      {template.source === 'custom' && canDeleteTemplate(template) && (
                        <button
                          type="button"
                          aria-label="Delete template"
                          onClick={() => setDeleteTarget(template)}
                          className="p-1.5 rounded-md text-muted-foreground hover:text-red-500 hover:bg-red-500/10 dark:text-[#8a8f98] transition-colors duration-150 flex-shrink-0"
                        >
                          <Trash className="h-4 w-4" weight="regular" />
                        </button>
                      )}
                    </div>

                    {/* Description */}
                    <p
                      className="text-muted-foreground dark:text-[#8a8f98] line-clamp-3 mb-3"
                      style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}
                    >
                      {template.description}
                    </p>

                    {/* Flow summary */}
                    <div className="px-2.5 py-1.5 rounded-md bg-muted/50 border border-border/40 dark:bg-white/[0.02] dark:border-white/[0.06] mb-3">
                      <p
                        className="flex items-center gap-1.5 text-muted-foreground dark:text-[#62666d]"
                        style={{
                          fontFamily: "'Berkeley Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
                          fontSize: '11px',
                          letterSpacing: '-0.05px',
                          lineHeight: 1.5,
                        }}
                      >
                        <ArrowRight className="h-3 w-3 flex-shrink-0" weight="regular" />
                        {template.flowSummary}
                      </p>
                    </div>

                    {/* Tags + node count */}
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      <span
                        className="px-2 py-0.5 rounded-full bg-muted/60 text-muted-foreground dark:bg-white/[0.04] dark:border dark:border-white/[0.06] dark:text-[#8a8f98]"
                        style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
                      >
                        {template.nodes.length} nodes
                      </span>
                      {template.tags.slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 rounded-full bg-muted/40 text-muted-foreground dark:bg-white/[0.03] dark:text-[#62666d]"
                          style={{ fontSize: '11px', letterSpacing: '-0.1px' }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* Use Template button */}
                    <div className="mt-auto pt-3 border-t border-border/50 dark:border-white/[0.06]">
                      <Button
                        variant="default"
                        size="sm"
                        className="w-full"
                        onClick={() => handleUseTemplate(template)}
                      >
                        <Play className="h-3.5 w-3.5 mr-1.5" weight="fill" />
                        Use template
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )
          })}
        </div>
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete this template?"
        description={deleteTarget ? `"${deleteTarget.name}" will be removed for your whole team. This can't be undone.` : undefined}
        confirmLabel="Delete"
        tone="destructive"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget?.templateId !== undefined && deleteMutation.mutate(deleteTarget.templateId)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  )
}

export default WorkflowTemplateGallery
